import { useEffect, useMemo, useRef } from 'react';
import * as Y from 'yjs';
import { canvasRoots } from '../wiring/canvas-roots';
import { useCanvasBinding } from '../wiring/contexts/canvas-binding-context';

/**
 * Per-user undo for a Canvas Y.Doc.
 *
 * `trackedOrigins` is a singleton Set containing only the local clientId, so
 * remote peers' writes (their own clientIds) and server-tagged writes
 * ('server:promotion', 'server:relation-mirror', 'server:gc') stay out of the
 * undo stack. Each editor in this engine tags its writes with `doc.clientID`
 * via `doc.transact(fn, doc.clientID)` — see StickyNode/TextNode for the pattern.
 *
 * Captures three top-level shared types: nodes, edges, zOrder. positions and
 * hidden are space-view-only (S9) and intentionally excluded for now.
 */
export interface CanvasUndoApi {
  undo: () => void;
  redo: () => void;
  canUndo: () => boolean;
  canRedo: () => boolean;
  manager: Y.UndoManager;
}

export function useCanvasUndoManager(doc: Y.Doc, clientId: number): CanvasUndoApi {
  const binding = useCanvasBinding();
  // Stable reference for the duration of (doc, clientId).
  const managerRef = useRef<Y.UndoManager | null>(null);

  const manager = useMemo(() => {
    const tracked = new Set<unknown>([clientId]);
    const { nodes, edges, zOrder } = canvasRoots(doc, binding);
    const um = new Y.UndoManager(
      [nodes, edges, zOrder],
      { trackedOrigins: tracked },
    );
    managerRef.current = um;
    return um;
  }, [doc, clientId, binding]);

  useEffect(() => {
    return () => {
      // Yjs UndoManager has no explicit destroy; nulling the ref is enough
      // to allow GC after the doc is torn down.
      managerRef.current = null;
    };
  }, [manager]);

  return useMemo(() => ({
    manager,
    undo: () => manager.undo(),
    redo: () => manager.redo(),
    canUndo: () => manager.canUndo(),
    canRedo: () => manager.canRedo(),
  }), [manager]);
}
